import { useState, useEffect, useCallback, useRef } from "react";
import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { Pencil, Play, X, Loader2, Trash2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface PolygonScanToolProps {
  onScanStarted?: (scanId: string) => void;
  isScanActive?: boolean;
  isSidebarOpen?: boolean;
}

export function PolygonScanTool({ onScanStarted, isScanActive, isSidebarOpen }: PolygonScanToolProps) {
  const map = useMap();
  const markerLib = useMapsLibrary("marker");
  const [mode, setMode] = useState<"idle" | "drawing" | "done">("idle");
  const [vertices, setVertices] = useState<google.maps.LatLngLiteral[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<string>("");
  const [collectionMode, setCollectionMode] = useState<"create" | "existing">("create");
  const [collectionName, setCollectionName] = useState("");
  const [existingCollectionId, setExistingCollectionId] = useState("");

  const polygonRef = useRef<google.maps.Polygon | null>(null);
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const clickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const dblClickListenerRef = useRef<google.maps.MapsEventListener | null>(null);

  const { data: configs } = trpc.scanConfigs.list.useQuery();
  const { data: collections } = trpc.collections.list.useQuery({}, { enabled: mode === "done" });
  const startScanMutation = trpc.scans.start.useMutation({
    onSuccess: (data) => {
      setCollectionName("");
      setExistingCollectionId("");
      onScanStarted?.(data.scanId);
    },
  });

  // Convert vertices to GeoJSON polygon
  const verticesToGeoJson = useCallback((verts: google.maps.LatLngLiteral[]) => {
    const ring = verts.map((v) => [v.lng, v.lat]);
    if (ring[0]) ring.push(ring[0]); // Close the ring
    return { coordinates: [ring] };
  }, []);

  // Clean up all map overlays
  const cleanup = useCallback(() => {
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }
    for (const marker of markersRef.current) {
      marker.map = null;
    }
    markersRef.current = [];
    if (clickListenerRef.current) {
      google.maps.event.removeListener(clickListenerRef.current);
      clickListenerRef.current = null;
    }
    if (dblClickListenerRef.current) {
      google.maps.event.removeListener(dblClickListenerRef.current);
      dblClickListenerRef.current = null;
    }
  }, []);

  // Create a draggable vertex marker
  const createVertexMarker = useCallback((
    position: google.maps.LatLngLiteral,
    index: number,
    isFirst: boolean,
    isClosed: boolean,
  ): google.maps.marker.AdvancedMarkerElement | null => {
    if (!map || !markerLib) return null;

    const div = document.createElement("div");
    div.style.width = isFirst && !isClosed ? "14px" : "10px";
    div.style.height = isFirst && !isClosed ? "14px" : "10px";
    div.style.borderRadius = "50%";
    div.style.backgroundColor = isFirst && !isClosed ? "#22c55e" : "#3b82f6";
    div.style.border = "2px solid white";
    div.style.cursor = "pointer";
    div.style.boxShadow = "0 1px 3px rgba(0,0,0,0.3)";

    const marker = new markerLib.AdvancedMarkerElement({
      position,
      map,
      content: div,
      gmpDraggable: isClosed,
      zIndex: isFirst ? 100 : 50,
    });

    // Handle drag for vertex editing (only when polygon is closed)
    if (isClosed) {
      marker.addListener("dragend", () => {
        const pos = marker.position;
        if (!pos) return;
        setVertices((prev) => {
          const updated = [...prev];
          updated[index] = { lat: pos.lat as number, lng: pos.lng as number };
          return updated;
        });
      });
    }

    return marker;
  }, [map, markerLib]);

  // Draw/update polygon and markers on map
  const renderPolygon = useCallback((verts: google.maps.LatLngLiteral[], closed: boolean) => {
    if (!map) return;

    // Clean previous overlays
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }
    for (const m of markersRef.current) {
      m.map = null;
    }
    markersRef.current = [];

    if (verts.length === 0) return;

    if (closed && verts.length >= 3) {
      polygonRef.current = new google.maps.Polygon({
        paths: verts,
        fillColor: "#3b82f6",
        fillOpacity: 0.15,
        strokeColor: "#3b82f6",
        strokeWeight: 2,
        strokeOpacity: 0.8,
        map,
      });
    } else if (verts.length >= 2) {
      polylineRef.current = new google.maps.Polyline({
        path: verts,
        strokeColor: "#3b82f6",
        strokeWeight: 2,
        strokeOpacity: 0.8,
        map,
      });
    }

    // Add vertex markers
    for (let i = 0; i < verts.length; i++) {
      const v = verts[i];
      if (!v) continue;
      const marker = createVertexMarker(v, i, i === 0, closed);
      if (marker) markersRef.current.push(marker);
    }
  }, [map, createVertexMarker]);

  // Re-render when vertices change
  useEffect(() => {
    renderPolygon(vertices, mode === "done");
  }, [vertices, mode, renderPolygon]);

  // Close the polygon
  const closePolygon = useCallback(() => {
    setMode("done");
    if (map) {
      map.setOptions({ draggableCursor: null, clickableIcons: true });
    }
  }, [map]);

  // Set up click listener for drawing
  useEffect(() => {
    if (!map || mode !== "drawing") return;

    map.setOptions({ draggableCursor: "crosshair", clickableIcons: false });

    clickListenerRef.current = map.addListener("click", (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return;
      const newPoint = { lat: e.latLng.lat(), lng: e.latLng.lng() };

      setVertices((prev) => {
        // Check if clicking near the first vertex to close
        if (prev.length >= 3) {
          const first = prev[0];
          if (first) {
            const dist = Math.sqrt(
              (newPoint.lat - first.lat) ** 2 + (newPoint.lng - first.lng) ** 2,
            );
            if (dist < 0.0005) {
              closePolygon();
              return prev;
            }
          }
        }
        return [...prev, newPoint];
      });
    });

    dblClickListenerRef.current = map.addListener("dblclick", (e: google.maps.MapMouseEvent) => {
      e.stop();
      setVertices((prev) => {
        if (prev.length >= 3) {
          closePolygon();
        }
        return prev;
      });
    });

    return () => {
      if (clickListenerRef.current) {
        google.maps.event.removeListener(clickListenerRef.current);
        clickListenerRef.current = null;
      }
      if (dblClickListenerRef.current) {
        google.maps.event.removeListener(dblClickListenerRef.current);
        dblClickListenerRef.current = null;
      }
      map.setOptions({ draggableCursor: null, clickableIcons: true });
    };
  }, [map, mode, closePolygon]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const enterDrawMode = useCallback(() => {
    cleanup();
    setVertices([]);
    setMode("drawing");
  }, [cleanup]);

  const clearAll = useCallback(() => {
    cleanup();
    setVertices([]);
    setMode("idle");
    if (map) {
      map.setOptions({ draggableCursor: null, clickableIcons: true });
    }
  }, [map, cleanup]);

  const handleStartScan = () => {
    if (!selectedConfigId || vertices.length < 3) return;

    const collection = collectionMode === "create"
      ? { mode: "create" as const, name: collectionName.trim() }
      : { mode: "existing" as const, collectionId: existingCollectionId };

    startScanMutation.mutate({
      configId: selectedConfigId,
      polygonOverride: verticesToGeoJson(vertices),
      collection,
    });
  };

  const isScanning = isScanActive || startScanMutation.isPending;

  const isCollectionValid =
    (collectionMode === "create" && collectionName.trim().length > 0) ||
    (collectionMode === "existing" && existingCollectionId.length > 0);

  return (
    <div
      className={`absolute top-3 left-0 right-0 z-10 flex flex-col items-center gap-2 ${
        isSidebarOpen ? "pr-[560px] lg:pr-[440px] xl:pr-[480px] 2xl:pr-[560px]" : ""
      }`}
    >
      {/* Draw button */}
      {mode === "idle" && (
        <Button
          size="sm"
          variant="secondary"
          className="shadow-lg bg-card shadow-md"
          onClick={enterDrawMode}
        >
          <Pencil className="mr-2 size-4" />
          Draw Area
        </Button>
      )}

      {/* Drawing instructions */}
      {mode === "drawing" && (
        <div className="rounded-lg bg-card shadow-md px-4 py-2 shadow-lg text-sm flex items-center gap-3">
          <span>
            {vertices.length === 0
              ? "Click to place first vertex"
              : vertices.length < 3
                ? `${vertices.length} vertices — keep clicking to add more`
                : `${vertices.length} vertices — click first vertex (green) or double-click to close`}
          </span>
          <Button size="sm" variant="ghost" onClick={clearAll}>
            <X className="size-4" />
          </Button>
        </div>
      )}

      {/* Polygon done — scan panel */}
      {mode === "done" && (
        <div className="rounded-lg bg-card shadow-md p-3 shadow-lg min-w-[280px]">
          {/* Collapsible form content — animates out when scanning */}
          <div
            className="grid transition-[grid-template-rows,opacity] duration-300 ease-in-out"
            style={{
              gridTemplateRows: isScanning ? "0fr" : "1fr",
              opacity: isScanning ? 0 : 1,
            }}
          >
            <div className="overflow-hidden">
              <div className="space-y-3 pb-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    Polygon: {vertices.length} vertices
                  </span>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={enterDrawMode}
                      title="Redraw"
                    >
                      <Pencil className="size-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={clearAll}
                      title="Clear"
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                </div>

                {configs && configs.length > 0 ? (
                  <>
                    <Select
                      value={selectedConfigId}
                      onValueChange={setSelectedConfigId}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Pick a config..." />
                      </SelectTrigger>
                      <SelectContent>
                        {configs.map((c) => (
                          <SelectItem key={c.id} value={c.id} className="text-xs">
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* Collection section */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Collection</Label>
                      <RadioGroup
                        value={collectionMode}
                        onValueChange={(v) => setCollectionMode(v as "create" | "existing")}
                        className="gap-2"
                      >
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="create" id="col-create" className="size-3.5" />
                          <Label htmlFor="col-create" className="text-xs font-normal cursor-pointer">
                            Create new collection
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="existing" id="col-existing" className="size-3.5" />
                          <Label htmlFor="col-existing" className="text-xs font-normal cursor-pointer">
                            Add to existing collection
                          </Label>
                        </div>
                      </RadioGroup>

                      {collectionMode === "create" ? (
                        <Input
                          value={collectionName}
                          onChange={(e) => setCollectionName(e.target.value)}
                          placeholder="Collection name..."
                          className="h-8 text-xs"
                        />
                      ) : (
                        <Select
                          value={existingCollectionId}
                          onValueChange={setExistingCollectionId}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder="Pick a collection..." />
                          </SelectTrigger>
                          <SelectContent>
                            {(collections ?? []).map((c) => (
                              <SelectItem key={c.id} value={c.id} className="text-xs">
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Create a scan config first to use Quick Scan.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Button — always visible */}
          {configs && configs.length > 0 && (
            <div className="space-y-2">
              <Button
                size="sm"
                className="w-full"
                disabled={!selectedConfigId || !isCollectionValid || startScanMutation.isPending || isScanActive}
                onClick={handleStartScan}
              >
                {isScanActive ? (
                  <>
                    <Loader2 className="mr-2 size-3.5 animate-spin" />
                    Scanning...
                  </>
                ) : (
                  <>
                    <Play className="mr-2 size-3.5" />
                    {startScanMutation.isPending ? "Starting..." : "Quick Scan"}
                  </>
                )}
              </Button>

              {startScanMutation.isError && !isScanning && (
                <p className="text-xs text-destructive">
                  {startScanMutation.error.message}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
